<template>
  <v-bottom-sheet v-model="dialogVisible" max-width="1920" class="energy-storage-detail-dialog">
    <div class="dialog-wrapper">
      <!-- 标题 -->
      <div class="dialog-title">储能系统详情</div>

      <!-- 主内容区域 -->
      <div class="dialog-main">
        <!-- 左侧：储能方阵内容区 -->
        <div class="left-content">
          <!-- 储能方阵折叠面板 - 使用自定义组件 -->
          <ArrayPanel v-for="(array, index) in arrayList" :key="array.id" :ref="el => arrayPanelRefs[index] = el"
            :array-data="array" :highlighted-device="highlightedDevice" :default-expanded="true"
            @open-elink-detail="openElinkDetail" @open-eblock-detail="openEblockDetail" @open-pcs-detail="openPcsDetail"
            @open-cluster-detail="openClusterDetail" />
        </div>

        <!-- 右侧：设备树 -->
        <div class="right-sidebar">
          <!-- 搜索框 -->
          <div class="search-wrapper">
            <v-text-field v-model="searchText" placeholder="按设备名称/UUID搜索设备" rounded variant="outlined" density="compact"
              append-inner-icon="mdi-magnify" clearable hide-details class="search-input"></v-text-field>
          </div>

          <!-- 设备树 -->
          <div class="device-tree">
            <v-treeview :items="treeItems" :search="searchText" :custom-filter="filterDevice" item-value="id"
              item-title="name" activatable open-all density="compact" class="custom-treeview"
              @update:activated="handleTreeItemClick">
              <template v-slot:prepend="{ item }">
                <img v-if="item.type === 'root'" src="@/assets/images/station-monitor/detail-dialog/tree-root-icon.png"
                  style="width: 16px; height: 14px;" alt="根节点" loading="lazy" />
                <img v-else-if="item.type === 'station'"
                  src="@/assets/images/station-monitor/detail-dialog/tree-station-icon.png"
                  style="width: 16px; height: 14px;" alt="电站" loading="lazy" />
                <img v-else-if="item.type === 'category'"
                  src="@/assets/images/station-monitor/detail-dialog/tree-category-icon.png"
                  style="width: 16px; height: 14px;" alt="分类" loading="lazy" />
                <img v-else src="@/assets/images/station-monitor/detail-dialog/tree-device-icon.png"
                  style="width: 16px; height: 14px;" alt="设备" loading="lazy" />
              </template>
            </v-treeview>
          </div>
        </div>
      </div>

      <!-- 关闭按钮 -->
      <v-btn icon size="small" variant="text" class="close-btn" @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </v-bottom-sheet>

  <!-- eLink 详情弹框 -->
  <ElinkDetailDialog v-model="elinkDetailDialogVisible" :elink="currentElinkDetail" />
  <!-- eBlock 详情弹框 -->
  <EblockDetailDialog v-model="eblockDetailDialogVisible" :eblock="currentEblockDetail" />
  <!-- PCS 详情弹框 -->
  <PcsDetailDialog v-model="pcsDetailDialogVisible" :block-data="currentPcsDetail" />
  <!-- Cluster 详情弹框 -->
  <ClusterDetailDialog v-model="clusterDetailDialogVisible" :cluster-data="currentClusterDetail" :eblock-id="currentClusterDetail?.id" />
</template>

<script setup>
import { ref, computed, watch, onUnmounted, shallowRef } from 'vue'
import ArrayPanel from './ArrayPanel.vue'
import { ws_req } from '@/ws/wss-request'
import useUserStore from '@/store/modules/user'
import { useStationContext } from '../../composables/useStationContext'
import ElinkDetailDialog from './ElinkDetailDialog.vue'
import EblockDetailDialog from './EblockDetailDialog.vue'
import PcsDetailDialog from './PcsDetailDialog.vue'
import ClusterDetailDialog from './ClusterDetailDialog.vue'
import {
  EBlockWorkStatus,
  EBlockStatusColor,
  getEBlockOnlineText,
  getEBlockOnlineClass,
  formatStatus
} from './monitorEnums'



// 格式化 eLink 数据 - 优化：只返回必要字段
const formatElink = (elink) => {
  const soc = elink.soc || 0
  return {
    id: elink.elink_id,
    uuid: elink.uuid,
    name: elink.elink_name,
    status: formatStatus(elink.run_st, EBlockWorkStatus),
    statusColor: EBlockStatusColor[elink.run_st] || EBlockStatusColor[1],
    power: elink.p_total ? `${elink.p_total}kW` : '0kW',
    socPercent: soc,
    model: elink.model
  }
}

// 格式化 Block 数据 - 优化：只返回必要字段
const formatBlock = (block) => {
  const soc = block.soc || 0
  return {
    id: block.eblock_id,
    uuid: block.uuid,
    name: block.eblock_name,
    model: block.model || '--',
    hasWarning: block.alarmNum > 0,
    alarmCount: block.alarmNum || 0,
    onlineText: getEBlockOnlineText(block.state_eblock),
    onlineClass: getEBlockOnlineClass(block.state_eblock),
    status: formatStatus(block.state_eblock, EBlockWorkStatus),
    statusColor: EBlockStatusColor[block.state_eblock] || EBlockStatusColor[1],
    power: block.p_ac ? `${block.p_ac}kW` : '0kW',
    soc: `${soc}%`,
    socPercent: `${soc}%`
  }
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const searchText = ref('')
let wsTimer = null

// 缓存上一次的结构Key，用于判断结构是否变化
const lastStructureKey = ref('')

// ArrayPanel 组件引用数组
const arrayPanelRefs = ref([])

// eLink详情弹框
const elinkDetailDialogVisible = ref(false)
const currentElinkDetail = ref(null)

const openElinkDetail = (elink) => {
  currentElinkDetail.value = elink
  elinkDetailDialogVisible.value = true
}

// eBlock详情弹框
const eblockDetailDialogVisible = ref(false)
const currentEblockDetail = ref(null)

const openEblockDetail = (block) => {
  currentEblockDetail.value = block
  eblockDetailDialogVisible.value = true
}

// PCS详情弹框
const pcsDetailDialogVisible = ref(false)
const currentPcsDetail = ref(null)

const openPcsDetail = (block) => {
  currentPcsDetail.value = block
  pcsDetailDialogVisible.value = true
}

// Cluster详情弹框
const clusterDetailDialogVisible = ref(false)
const currentClusterDetail = ref(null)

const openClusterDetail = (block) => {
  // block 结构通常包含 uuid, code, id 等
  // 这里暂时直接传递 block 数据，ClusterDetailDialog 内部可能需要根据 block ID 自行获取详情
  // 或者在此处调用 API 获取详情后再赋值
  currentClusterDetail.value = block
  clusterDetailDialogVisible.value = true
}

// 高亮的设备ID和类型
const highlightedDevice = ref({ type: null, id: null })

// 使用 composable 获取电站上下文信息
const { stationId: currentStationId, stationName: currentStationName } = useStationContext()

// 树形数据 - 使用 shallowRef 减少深度响应式开销
const treeItems = shallowRef([])

// 储能方阵数据 - 使用 shallowRef 减少深度响应式开销
const arrayList = shallowRef([])

// WebSocket 获取数据 - 优化：减少不必要的数组操作和DOM更新
const fetchElinkEblockOverview = async (recurs = true) => {
  clearTimeout(wsTimer)

  if (!currentStationId.value) {
    console.warn('stationId is not available')
    return
  }

  try {
    // 检查连接状态
    if (!ws_req.is_open && !ws_req.is_connecting) {
      const userStore = useUserStore()
      await userStore.getInfo()
    }

    const result = await ws_req.request('elink_eblock_overview', {
      stationId: Number(currentStationId.value)
    })

    if (result && Array.isArray(result)) {
      // 生成结构指纹：elink_id + block数量 + block_ids
      const currentStructureKey = result.map(item => {
        const eid = item.eLink?.elink_id || 'x'
        const bCount = item.eBlockList?.length || 0
        const bIds = item.eBlockList?.map(b => b.eblock_id).join(',') || ''
        return `${eid}-${bCount}-${bIds}`
      }).join('|')

      if (currentStructureKey === lastStructureKey.value) {
        // 结构未变，仅更新数值（性能优化：批量更新，减少响应式触发）
        const newArrayList = [...arrayList.value]
        let hasChanges = false

        result.forEach((item, index) => {
          if (newArrayList[index]) {
            const target = newArrayList[index]
            // 更新 eLink
            if (item.eLink) {
              const newElinkData = formatElink(item.eLink)
              if (JSON.stringify(target.elink) !== JSON.stringify(newElinkData)) {
                target.elink = newElinkData
                hasChanges = true
              }
            }

            // 更新 Blocks
            if (item.eBlockList && Array.isArray(item.eBlockList)) {
              item.eBlockList.forEach((block, bIndex) => {
                if (target.blocks[bIndex]) {
                  const newBlockData = formatBlock(block)
                  if (JSON.stringify(target.blocks[bIndex]) !== JSON.stringify(newBlockData)) {
                    target.blocks[bIndex] = newBlockData
                    hasChanges = true
                  }
                }
              })
            }
          }
        })

        // 只有数据真正变化时才触发更新
        if (hasChanges) {
          arrayList.value = newArrayList
        }
      } else {
        // 结构变化，全量更新
        const newArrayList = result.map((item, index) => {
          const elink = item.eLink || {}
          const eBlockList = item.eBlockList || []

          return {
            id: elink.elink_id || index + 1,
            name: `${index + 1}#储能方阵`,
            elink: formatElink(elink),
            blocks: eBlockList.map(formatBlock)
          }
        })

        arrayList.value = newArrayList
        lastStructureKey.value = currentStructureKey

        // 仅在结构变化时重建树（树形数据不会变化，只构建一次）
        buildTreeItems(result)
      }
    }
  } catch (e) {
    if (e.message && e.message.includes('WebSocket 未连接')) {
      console.warn('EnergyStorageDetail WS not connected, retrying later...')
    } else {
      console.error('EnergyStorageDetail WS request failed:', e)
    }
  }

  if (recurs && dialogVisible.value) {
    wsTimer = setTimeout(() => {
      fetchElinkEblockOverview(true)
    }, 5000)
  }
}

// 构建树形结构数据
const buildTreeItems = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    treeItems.value = []
    return
  }

  // 构建树：电站 -> 方阵(eLink) -> eBlock列表
  // 使用注入的电站名称
  const stationName = currentStationName.value

  const children = data.map((item, index) => {
    const elink = item.eLink || {}
    const eBlockList = item.eBlockList || []

    return {
      id: `array-${elink.elink_id || index}`,
      name: `${index + 1}#储能方阵`,
      type: 'category',
      children: [
        // eLink节点
        {
          id: `elink-${elink.elink_id}`,
          name: elink.elink_name,
          type: 'elink',
          uuid: elink.uuid
        },
        // eBlock节点
        ...eBlockList.map(block => ({
          id: `eblock-${block.eblock_id}`,
          name: block.eblock_name,
          type: 'eblock',
          uuid: block.uuid
        }))
      ]
    }
  })

  treeItems.value = [
    {
      id: 'station-root',
      name: stationName,
      type: 'station',
      children: children
    }
  ]
}



// 监听弹框打开状态，打开时获取数据
watch(dialogVisible, (newVal) => {
  if (newVal && currentStationId.value) {
    fetchElinkEblockOverview(true)
  } else {
    clearTimeout(wsTimer)
  }
})

// 监听电站ID变化
watch(currentStationId, (newVal) => {
  if (newVal) {
    // 切换电站时重置状态
    lastStructureKey.value = ''

    if (dialogVisible.value) {
      fetchElinkEblockOverview(true)
    }
  }
})

const closeDialog = () => {
  dialogVisible.value = false
}

// 处理树节点点击事件
const handleTreeItemClick = (selectedItems) => {
  console.log('handleTreeItemClick called, selectedItems:', selectedItems)

  // selectedItems 可能是数组或对象，需要处理
  let itemId = null

  if (Array.isArray(selectedItems)) {
    if (selectedItems.length === 0) return
    itemId = selectedItems[0]
  } else if (selectedItems) {
    // 可能直接传递了单个项
    itemId = selectedItems
  } else {
    return
  }

  console.log('itemId:', itemId)

  // 从 treeItems 中查找对应的项
  const findItem = (items, targetId) => {
    for (const item of items) {
      if (item.id === targetId) return item
      if (item.children) {
        const found = findItem(item.children, targetId)
        if (found) return found
      }
    }
    return null
  }

  const item = findItem(treeItems.value, itemId)
  console.log('found item:', item)

  if (!item || !item.type) return

  // 只处理 elink 和 eblock 类型
  if (item.type === 'elink' || item.type === 'eblock') {
    // 提取设备ID
    const deviceId = item.type === 'elink'
      ? parseInt(item.id.replace('elink-', ''))
      : parseInt(item.id.replace('eblock-', ''))

    console.log('deviceId:', deviceId, 'type:', item.type)

    // 查找设备所在的面板索引
    const panelIndex = arrayList.value.findIndex((array) => {
      if (item.type === 'elink') {
        return array.elink.id === deviceId
      } else {
        return array.blocks.some(block => block.id === deviceId)
      }
    })

    console.log('panelIndex:', panelIndex)

    if (panelIndex !== -1) {
      // 展开所有面板
      arrayPanelRefs.value.forEach(panel => {
        if (panel && panel.expand) {
          panel.expand()
        }
      })

      // 设置高亮设备
      highlightedDevice.value = { type: item.type, id: deviceId }
      console.log('highlightedDevice set to:', highlightedDevice.value)

      // 等待面板展开动画完成后再滚动到对应设备
      setTimeout(() => {
        const selector = item.type === 'elink'
          ? `.elink-info-card[data-elink-id="${deviceId}"]`
          : `.block-card[data-block-id="${deviceId}"]`

        console.log('looking for element with selector:', selector)
        const element = document.querySelector(selector)
        console.log('found element:', element)

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 400)

      // 3秒后取消高亮
      setTimeout(() => {
        highlightedDevice.value = { type: null, id: null }
      }, 3000)
    }
  }
}

// 自定义搜索过滤器，支持搜索名称和UUID
const filterDevice = (value, search, item) => {
  if (!search) return true
  const searchLower = search.toLowerCase()

  // 获取名称 (value 通常是 item-title 对应的值)
  const text = (value || '').toLowerCase()

  // 获取UUID
  // item 可能是原始数据对象，也可能是包含 raw 属性的包装对象
  const rawItem = item.raw || item
  const uuid = (rawItem.uuid || '').toLowerCase()

  return text.includes(searchLower) || uuid.includes(searchLower)
}

// 组件卸载时清理定时器
onUnmounted(() => {
  clearTimeout(wsTimer)
})
</script>

<style scoped lang="scss">
.energy-storage-detail-dialog {
  :deep(.v-overlay__content) {
    width: 100vw !important;
    max-width: 1920px !important;
    height: 950px !important;
    margin: 0 auto !important;
    border-radius: 30px 30px 0 0 !important;
  }
}

.dialog-wrapper {
  position: relative;
  width: 100%;
  height: 950px;
  padding: 20px 24px 0;
  background-color: #fff !important;
  background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
  background-size: cover;
  border-radius: 30px 30px 0 0 !important;
}

.dialog-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  margin-bottom: 20px;
  padding-left: 24px;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
}

.dialog-main {
  display: flex;
  gap: 30px;
  height: calc(100% - 83px);
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 20px;
}

/* 左侧内容区 - 性能优化 */
.left-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* 开启硬件加速，优化滚动性能 */
  will-change: scroll-position;
  transform: translateZ(0);
  -webkit-overflow-scrolling: touch;
  /* 确保flex容器可以滚动 */
  min-height: 0;
}

/* 右侧边栏 */
.right-sidebar {
  width: 319px;
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.search-wrapper {
  position: relative;
  margin-bottom: 26px;
}

.search-input {
  width: 100%;
}

.device-tree {
  flex: 1;
  overflow-y: auto;
}

.custom-treeview {
  :deep(.v-treeview-item) {
    margin-bottom: 8px;
  }

  :deep(.v-treeview-item__label) {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    color: rgba(0, 0, 0, 1);
    line-height: normal;
  }

  :deep(.v-treeview-item--active) {
    background: rgba(239, 242, 254, 1);
    border-radius: 3px;

    .v-treeview-item__label {
      color: rgba(58, 104, 255, 1);
    }
  }

  :deep(.v-treeview-item__prepend) {
    margin-right: 9px;
  }
}
</style>
